import { useState, useEffect } from 'react';
import { authService } from '@/services/authService';
import { BaseProfile, BuyerProfile } from '@/hooks/useProfile';
import { API_BASE_URL } from '@/config/api';

// Global singleton state
let profilePromise: Promise<any> | null = null;
// Attempt to hydrate from localStorage cache for instant first paint
let profileData: {
  base: BaseProfile | null;
  buyer: BuyerProfile | null;
  profileCompletion?: { isComplete: boolean; missingFields: string[] } | null;
} | null = null;

try {
  const cached = JSON.parse(localStorage.getItem('fb_profile_cache') || 'null');
  if (cached && cached.base) {
    profileData = cached;
  }
} catch {}


export const useProfileSingleton = () => {
  const [data, setData] = useState(profileData);
  const [loading, setLoading] = useState(!profileData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }
    if (!profileData && !profilePromise) {
      profilePromise = fetchProfileData()
        .then(result => {
          profileData = result;
          setData(result);
          setLoading(false);
          setError(null);
          return result;
        })
        .catch(err => {
          console.error('Profile fetch failed:', err);
          setError('Failed to load profile');
          setLoading(false);
          profilePromise = null; // Allow retry
          throw err;
        });
    } else if (profilePromise) {
      profilePromise
        .then(result => {
          setData(result);
          setLoading(false);
          setError(null);
        })
        .catch(err => {
          console.error('Profile fetch failed:', err);
          setError('Failed to load profile');
          setLoading(false);
        });
    } else if (profileData) {
      setData(profileData);
      setLoading(false);
    }

    // Listen for optimistic updates
    const handleProfileUpdate = () => {
      setData({ ...profileData });
    };
    
    window.addEventListener('profile-updated', handleProfileUpdate);
    return () => window.removeEventListener('profile-updated', handleProfileUpdate);
  }, []);

  const refreshProfile = async () => {
    try {
      setLoading(true);
      profilePromise = null; // Clear cache
      profileData = null;
      
      const result = await fetchProfileData();
      profileData = result;
      setData(result);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Profile refresh failed:', err);
      setError('Failed to refresh profile');
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    base: data?.base || null,
    buyer: data?.buyer || null,
    profileCompletion: data?.profileCompletion || null,
    refreshProfile,
  };
};

// Internal fetch function
async function fetchProfileData() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';
  
  const resp = await fetch(`${API_BASE_URL}/api/v2/auth-2/profile`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'x-platform': 'fabrics',
      'x-current-role': 'fabric-buyer',
    },
  });
  if (!resp.ok) {
    throw new Error(`Profile fetch failed (${resp.status})`);
  }
  const fullJson = await resp.json();

  const baseProfile = fullJson?.data?.baseProfile || {};
  const buyerProfile = fullJson?.data?.roleSpecificProfile?.profileFields || {};
  const completion = fullJson?.data?.profileCompletion || null;

  const result = {
    base: baseProfile as BaseProfile,
    buyer: buyerProfile as BuyerProfile,
    profileCompletion: completion,
  };
  try {
    localStorage.setItem('fb_profile_cache', JSON.stringify(result));
  } catch {}
  return result;
}

// Optimistic update function
export const updateProfileOptimistically = (updates: Partial<any>) => {
  if (profileData) {
    profileData = {
      ...profileData,
      base: profileData.base ? { ...profileData.base, ...updates } : profileData.base,
      buyer: profileData.buyer ? { ...profileData.buyer, ...updates } : profileData.buyer,
    };
    
    // Trigger re-render in all components using the hook
    window.dispatchEvent(new CustomEvent('profile-updated'));
  }
};

// Background refresh (no loading state) – throttled to once per minute
let lastBackgroundRefresh = 0;
export const refreshProfileInBackground = async () => {
  const now = Date.now();
  if (now - lastBackgroundRefresh < 60_000) {
    return; // skip – refreshed recently
  }
  lastBackgroundRefresh = now;

  try {
    const result = await fetchProfileData();
    profileData = result;
    window.dispatchEvent(new CustomEvent('profile-updated'));
  } catch (err) {
    console.error('Background profile refresh failed:', err);
  }
};

// Clear cache function for logout/auth changes
export const clearProfileCache = () => {
  profilePromise = null;
  profileData = null;
};
